import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import GlobalLayout from './GlobalLayout';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useExternalBackButton } from '../hooks/useExternalBackButton';
import { getSecureMediaUrl } from '../utils/mediaUtils';
import './ElementPage.css';

// --- NEU: Helper Funktion für Mehrsprachigkeit ---
const getLocalizedContent = (item, field, langCode) => {
  if (!item) return '';
  // Sprachecode extrahieren (z.B. "en-US" -> "en")
  const code = langCode ? String(langCode).split('-')[0].toLowerCase() : 'de';
  
  // Wenn Sprache Deutsch ist, Standardfeld nehmen (oder explizit name_de falls vorhanden)
  // Hier gehen wir davon aus, dass das Suffix-Feld (z.B. name_en) existiert.
  const localizedField = `${field}_${code}`;
  
  // Prüfen ob das feld_lang (z.B. name_en) existiert und nicht leer/null ist
  if (item[localizedField] && String(item[localizedField]).trim() !== '') {
    return item[localizedField];
  }
  
  // Fallback auf Default Feld (z.B. name), was meistens Deutsch ist
  return item[field] || '';
};

const formatMinutes = (seconds) => {
  if (!seconds && seconds !== 0) return '';
  const mins = Math.max(0, Math.floor(Number(seconds) / 60));
  return `${mins} min`;
};

const formatTime = (seconds) => {
  if (!seconds && seconds !== 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatDate = (dateStr, langCode) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  const code = langCode ? String(langCode).split('-')[0].toLowerCase() : 'de';
  const localeMap = { de: 'de-DE', en: 'en-GB', fr: 'fr-FR', es: 'es-ES', it: 'it-IT' };
  const locale = localeMap[code] || 'de-DE';
  return date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const ElementPage = () => {
  const { hashId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getElement, toggleFavourites } = useAuth();
  
  // --- ÄNDERUNG: currentLanguage hier holen ---
  const { t, currentLanguage } = useLanguage();
  
// --- FIX START: Ziel VORHER berechnen ---
  // Wir ermitteln das Ziel, BEVOR wir den Hook aufrufen.
  const backTarget = useMemo(() => {
    // Prio 1: QR Scan
    if (location?.state?.fromQRScan) return '/my-courses';
    // Prio 2: Übergebene SourcePage (z.B. '/my-courses' oder '/dashboard')
    if (location?.state?.sourcePage) return location.state.sourcePage;
    // Fallback
    return '/dashboard';
  }, [location]);

  // Jetzt übergeben wir das dynamische Ziel an den Hook
  const { handleBackClick: defaultBackClick } = useExternalBackButton(backTarget);
  // --- FIX ENDE ---

  // --- FIX: Custom Back Handler ---
  const handleCustomBack = () => {
    // Prio 1: Rückkehr aus Chat-Navigation → Dashboard mit restoreChat-Signal
    if (location.state?.openChatOnReturn) {
      const target = location.state.sourcePage || '/dashboard';
      navigate(target, { state: { restoreChat: true } });
      return;
    }
    // Prio 2: Search-Context wiederherstellen
    if (location.state?.searchContext && location.state?.sourcePage) {
      navigate(location.state.sourcePage, { 
        state: { 
          searchContext: location.state.searchContext 
        } 
      });
    } else {
      // Standard-Verhalten
      defaultBackClick();
    }
  };

  const initialFromState = useMemo(() => {
    const s = location?.state?.elementData;
    return s && typeof s === 'object' ? s : null;
  }, [location]);

  const fromQRScan = useMemo(() => {
    return Boolean(location?.state?.fromQRScan);
  }, [location]);

  // --- FIX: Source Page aus State lesen ---
  const sourcePage = useMemo(() => {
    return location?.state?.sourcePage;
    
  }, [location]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [element, setElement] = useState(initialFromState);
  const [chapters, setChapters] = useState([]);
  const [related, setRelated] = useState([]);
  const [tags, setTags] = useState([]);
  const [isFavorite, setIsFavorite] = useState(Boolean(initialFromState?.favourite_value === 1));
  const containerRef = useRef(null);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  // Video-Bereitschafts-State: null = unbekannt, true = bereit, false = wird generiert
  const [videoReady, setVideoReady] = useState(null);
  const pollRef = useRef(null);

  // ── NiimBot Drucker-State ──────────────────────────────────────────────────
  const [printerClient,    setPrinterClient]    = useState(null);
  const [printerConnected, setPrinterConnected] = useState(false);
  const [printStatus,      setPrintStatus]      = useState('idle'); // 'idle'|'connecting'|'printing'|'done'|'error'
  const printCanvasRef = useRef(null);
  // Feature-Detection: Web Bluetooth API + Hardware-Verfügbarkeit prüfen
  // getAvailability() liefert false wenn Bluetooth am Gerät deaktiviert ist,
  // auch wenn die Browser-API vorhanden ist.
  const [niimbotAvailable, setNiimbotAvailable] = useState(false);
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.bluetooth) return;
    navigator.bluetooth.getAvailability()
      .then(available => setNiimbotAvailable(available))
      .catch(() => setNiimbotAvailable(false));
    // Auf Bluetooth-Statusänderungen reagieren (z.B. BT wird ein-/ausgeschaltet)
    const handler = (e) => setNiimbotAvailable(e.value);
    navigator.bluetooth.addEventListener?.('availabilitychanged', handler);
    return () => navigator.bluetooth.removeEventListener?.('availabilitychanged', handler);
  }, []);

  
  const hasMandatory = useMemo(() => {
    return (chapters || []).some((c) => String(c.is_mandatory) === '1');
  }, [chapters]);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const res = await getElement(hashId);
        const payload = res || {};
        const data = payload.data || {};
        if (!isMounted) return;
        setElement(data);
        const ch = Array.isArray(payload.chapters) ? payload.chapters : [];
        // Normalize numeric start_time and sort by order/start_time
        const normalized = ch
          .map((c) => ({ ...c, _start: Math.max(0, Number(c.start_time || 0)) }))
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a._start - b._start);
        setChapters(normalized);
        const rel = Array.isArray(payload.related) 
          ? [...payload.related].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          : [];
        setRelated(rel);
        // Normalize tags from payload or element
        const rawTags = Array.isArray(payload.tags) ? payload.tags : (Array.isArray(data.tags) ? data.tags : []);
        const normalizedTags = rawTags.map((t) => {
          if (!t) return null;
          if (typeof t === 'string') return t;
          if (typeof t === 'object') return t.name || t.label || null;
          return null;
        }).filter(Boolean);
        setTags(normalizedTags);
        setIsFavorite(data?.favourite_value === 1);
        setError(null);
      } catch (e) {
        if (!isMounted) return;
        setError('Failed to load element');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, [getElement, hashId]);

  // Polling: prüft alle 8s ob url_element gefüllt wurde (Video fertig generiert)
  const startVideoPoll = useCallback((hId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    let attempts = 0;
    const MAX_ATTEMPTS = 20; // 20 × 8s = ~2:40 min
    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const res = await getElement(hId);
        const data = res?.data || {};
        if (data.url_element) {
          setElement(data);
          setVideoReady(true);
          clearInterval(pollRef.current);
          pollRef.current = null;
        } else if (attempts >= MAX_ATTEMPTS) {
          // Timeout: Video-Generierung dauert zu lang, stoppe Polling
          setVideoReady(false);
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (_) { /* Netzwerkfehler ignorieren, nächsten Versuch abwarten */ }
    }, 8000);
  }, [getElement]);

  useEffect(() => {
    // Poll starten wenn Element geladen aber noch kein Video verfügbar
    if (element && !element.url_element) {
      setVideoReady(null); // Spinner zeigen
      startVideoPoll(hashId);
    } else if (element && element.url_element) {
      setVideoReady(true);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [element?.url_element]); // eslint-disable-line react-hooks/exhaustive-deps

  const onToggleFavourite = async () => {
    try {
      const res = await toggleFavourites(hashId);
      const favVal = Number(res?.data?.favourite_value);
      if (Number.isFinite(favVal)) {
        setIsFavorite(favVal === 1);
      } else {
        setIsFavorite((prev) => !prev);
      }
    } catch (_) {}
  };

  const handlePlayButton = (chapterStartTime = null) => {
    if (chapterStartTime && typeof chapterStartTime === 'object') {
      chapterStartTime = null;
    }
    // Build a robust playlist of lessons:
    // 1) If collectionData.elements exists, prefer it
    // 2) Else use related
    // 3) Ensure current element is included exactly once
    const collectionLessons = Array.isArray(location?.state?.collectionData?.elements)
      ? location.state.collectionData.elements
      : [];
    const baseLessons = collectionLessons.length > 0 ? collectionLessons : (Array.isArray(related) ? related : []);

    // Deduplicate by hash_id and ensure current element is present
    const mapByHash = new Map();
    const pushUnique = (item) => {
      const hid = item?.hash_id || item?.hashId || item?.id;
      if (!hid) return;
      if (!mapByHash.has(hid)) mapByHash.set(hid, item);
    };

    baseLessons.forEach(pushUnique);
    if (element) pushUnique(element);

    const lessons = Array.from(mapByHash.values());
    const currentIndex = Math.max(0, lessons.findIndex(l => (l?.hash_id || l?.hashId || l?.id) === hashId));

    navigate(`/element-feed/${hashId}`, {
      state: {
        lessons,
        startIndex: currentIndex >= 0 ? currentIndex : 0,
        collectionData: location?.state?.collectionData || null,
        sourcePage: location?.state?.sourcePage,
        chapterStartTime,
        // Chat-Restore-Kette: durchreichen damit ElementFeed weiß, woher wir kommen
        openChatOnReturn: location?.state?.openChatOnReturn || false,
      }
    });
  };

  const handleChapterClick = (chapter) => {
    const startTime = Number(chapter.start_time || 0);
    handlePlayButton(startTime);
  };

  // Swipe: left -> open first related (next), right -> back
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onTouchStart = (e) => {
      try { 
        touchStartX.current = e.changedTouches[0].clientX;
        touchStartY.current = e.changedTouches[0].clientY;
      } catch (_) { 
        touchStartX.current = null;
        touchStartY.current = null;
      }
    };
    const onTouchEnd = (e) => {
      if (touchStartX.current == null || touchStartY.current == null) return;
      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const deltaX = endX - touchStartX.current;
      const deltaY = endY - touchStartY.current;
      const threshold = 60;
      
      // Only trigger horizontal swipe if it's clearly horizontal (deltaX is significantly larger than deltaY)
      // and the horizontal movement is substantial enough
      if (Math.abs(deltaX) > Math.abs(deltaY) * 2 && Math.abs(deltaX) > threshold) {
        if (deltaX <= -threshold) {
          const next = Array.isArray(related) && related.length > 0 ? related[0] : null;
          if (next?.hash_id) {
            navigate(`/element/${next.hash_id}`, { state: { elementData: next } });
          }
        } else if (deltaX >= threshold) {
          handleCustomBack();
        }
      }
      
      touchStartX.current = null;
      touchStartY.current = null;
    };
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [related, navigate]);

  // ── NiimBot Druckfunktionen ────────────────────────────────────────────────
  const loadImageToCanvas = useCallback((imageUrl) => {
    return new Promise((resolve, reject) => {
      const LABEL_W = 400;
      const LABEL_H = 240;
      const canvas  = printCanvasRef.current;
      canvas.width  = LABEL_W;
      canvas.height = LABEL_H;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, LABEL_W, LABEL_H);
      const drawQR = (img) => {
        const padding = 10;
        const size = LABEL_H - padding * 2;
        const x = Math.round((LABEL_W - size) / 2);
        ctx.drawImage(img, x, padding, size, size);
        resolve(canvas);
      };
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => drawQR(img);
      img.onerror = () => {
        fetch(imageUrl)
          .then(r => r.blob())
          .then(blob => {
            const blobUrl = URL.createObjectURL(blob);
            const img2 = new Image();
            img2.onload = () => { drawQR(img2); URL.revokeObjectURL(blobUrl); };
            img2.onerror = () => reject(new Error('Bild konnte nicht geladen werden'));
            img2.src = blobUrl;
          })
          .catch(reject);
      };
      img.src = imageUrl;
    });
  }, []);

  const printQRNiimbot = useCallback(async () => {
    const qrUrl = element?.qr_direct_url;
    if (!qrUrl) return;
    let lib;
    try {
      lib = await import('@mmote/niimbluelib');
    } catch (err) {
      console.error('[Niimbot] Library konnte nicht geladen werden:', err);
      setPrintStatus('error');
      return;
    }
    const { NiimbotBluetoothClient, ImageEncoder, Abstraction, B1PrintTask } = lib;
    let client = printerClient;
    if (!printerConnected || !client) {
      setPrintStatus('connecting');
      try {
        client = new NiimbotBluetoothClient();
        client.on('disconnect', () => {
          setPrinterConnected(false);
          setPrinterClient(null);
          setPrintStatus('idle');
        });
        await client.connect();
        setPrinterClient(client);
        setPrinterConnected(true);
      } catch (err) {
        console.error('[Niimbot] Verbindung fehlgeschlagen:', err);
        setPrintStatus('error');
        return;
      }
    }
    setPrintStatus('printing');
    try {
      const canvas = await loadImageToCanvas(getSecureMediaUrl(qrUrl));
      const labelProps = { width: canvas.width, height: canvas.height, printDirection: 'left' };
      const encoded   = ImageEncoder.encodeCanvas(canvas, labelProps);
      const abstraction = new Abstraction(client);
      const task = new B1PrintTask(abstraction);
      await task.printInit(encoded, 1);
      await task.printPage(encoded, 1);
      await task.waitForFinished();
      setPrintStatus('done');
    } catch (err) {
      console.error('[Niimbot] Druckfehler:', err);
      setPrintStatus('error');
    }
  }, [element, printerClient, printerConnected, loadImageToCanvas]);

  const printQROsDialog = useCallback(() => {
    const qrUrl = element?.qr_direct_url;
    if (!qrUrl) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html><head><title>${element.name || 'QR-Code'}</title>
      <style>
        body { margin: 0; display: flex; flex-direction: column; align-items: center;
               justify-content: center; min-height: 100vh; font-family: sans-serif; }
        img  { width: 280px; height: 280px; }
        h2   { margin-bottom: 16px; font-size: 18px; text-align: center; }
      </style></head>
      <body>
        <h2>${element.name || ''}</h2>
        <img src="${getSecureMediaUrl(qrUrl)}" alt="QR-Code" />
      </body></html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  }, [element]);


  if (loading) {
    return (
      <GlobalLayout 
        title="" 
        showBackButton 
        onBackClick={handleCustomBack}
        showNavbar
      >
        <div className="mobile-container">
          <div className="loading-screen">
            <div className="spinner"></div>
            <p>{t('loading') || 'Loading...'}</p>
          </div>
        </div>
      </GlobalLayout>
    );
  }

  if (error) {
    return (
      <GlobalLayout 
        title="" 
        showBackButton 
        onBackClick={handleCustomBack}
        showNavbar
      >
        <div className="mobile-container">
          <div className="error-screen">
            <p>{error}</p>
            <button className="btn" onClick={handleCustomBack}>{t('back') || 'Back'}</button>
          </div>
        </div>
      </GlobalLayout>
    );
  }

  if (!element) return null;

  // --- NEU: Lokalisierte Texte vorbereiten ---
  const displayTitle = getLocalizedContent(element, 'name', currentLanguage) || element.name;
  const displayDesc = getLocalizedContent(element, 'description', currentLanguage) || element.description;

  return (
    <GlobalLayout 
      title={displayTitle || ''} 
      showBackButton 
      onBackClick={handleCustomBack}
      backTo={backTarget}
      showNavbar
    >
      <div className="element-page" ref={containerRef}>

      <section className="ep-hero">
        <div className="element-page" ref={containerRef} style={{ paddingTop: '80px' }}>  
          {/* ÄNDERUNG: Platzhalter anzeigen, wenn kein Thumbnail vorhanden ist */}
          {element.url_thumbnail ? (
            <img className="ep-hero-img" src={getSecureMediaUrl(element.url_thumbnail)} alt={displayTitle} />
          ) : (
            <div 
              className="ep-hero-img" 
              style={{
                width: '100%',
                aspectRatio: '9/16', // Standard-Video-Format als Platzhalter
                backgroundColor: 'rgba(255,255,255,0.05)', // Dezent grauer Hintergrund
                display: 'block'
              }} 
            />
          )}

          {hasMandatory && (
            <div className="ep-hero-badges-left">
              <span className="badge">{t('mandatory') || 'Mandatory'}</span>
            </div>
          )}
          
        </div>
        {/* Play-Button nur wenn Video bereit; sonst Generating-Spinner */}
        {videoReady === false ? (
          <div style={{
            position: 'absolute', left: '50%', top: '50%',
            transform: 'translate(-50%,-50%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px'
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: '50%',
              border: '3px solid rgba(255,255,255,0.3)',
              borderTopColor: '#FF6B00',
              animation: 'spin 1s linear infinite'
            }} />
            <span style={{ color: '#fff', fontSize: 12, fontWeight: 600,
              textShadow: '0 1px 3px rgba(0,0,0,0.6)', textAlign: 'center', maxWidth: 140 }}>
              {t('videoGenerating') || 'Video wird erstellt…'}
            </span>
          </div>
        ) : videoReady === null && !element?.url_element ? (
          <div style={{
            position: 'absolute', left: '50%', top: '50%',
            transform: 'translate(-50%,-50%)'
          }}>
            <div className="spinner" style={{ borderTopColor: '#FF6B00', width: 44, height: 44 }} />
          </div>
        ) : (
          <button className="hero-play" onClick={() => handlePlayButton()} aria-label={t('play') || 'Play'}>
            <svg width="72" height="72" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="12" fill="#FFFFFF" />
              <path d="M10 8 L16 12 L10 16 Z" fill="#FF6B00" />
            </svg>
          </button>
        )}
        {/* Progress Bar - Always Visible */}
        <div className="ef-progress" style={{
          position: 'absolute',
          bottom: '0px',
          left: '0px',
          right: '0px',
          zIndex: 10
        }}>
          <div 
            className="ef-progress-fill"
            style={{
              width: `${Math.min(100, Math.max(0, Number(element?.progress || 0)))}%`
            }}
          />
        </div>
        
        {/* Time Display - Always Visible */}
        <div style={{
          position: 'absolute',
          bottom: '8px',
          left: '12px',
          color: '#fff',
          fontSize: '12px',
          fontWeight: '600',
          textShadow: '0 2px 4px rgba(0,0,0,0.8), 0 1px 2px rgba(0,0,0,0.6)',
          zIndex: 11
        }}>
          {formatTime((Number(element?.progress || 0) / 100) * (Number(element?.duration || 0)))} / {formatTime(Number(element?.duration || 0))}
        </div>
      </section>

      <section className="ep-card ep-title-block">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
            <h1 className="ep-title" style={{ margin: 0, flex: 1 }}>{displayTitle}</h1>
            
            <button 
                onClick={onToggleFavourite} 
                aria-label={isFavorite ? (t('unfavorite') || 'Unfavorite') : (t('favorite') || 'Favorite')} 
                style={{ 
                    background: 'transparent', 
                    border: 'none', 
                    fontSize: '28px', 
                    lineHeight: 1,
                    color: isFavorite ? '#FF6407' : '#D1D5DB', // Orange wenn aktiv, Grau wenn inaktiv
                    cursor: 'pointer',
                    padding: '0 0 0 12px'
                }}
            >
                ★
            </button>
        </div>
        
        <div className="ep-meta">
          <span className="meta meta--duration" aria-label={t('duration') || 'Duration'}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M12 7v6l4 2" stroke="#EA580C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12" cy="12" r="9" stroke="#EA580C" strokeWidth="2" fill="none"/>
            </svg>
            {Number(element.duration) < 60 
              ? `${Number(element.duration || 0)} sec` 
              : `${formatTime(element.duration)} min`
            }
          </span>
          <span className="meta" aria-label={t('number_of_views') || 'Views'}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12" cy="12" r="3" stroke="#6B7280" strokeWidth="2"/>
            </svg>
            {element.number_of_views}
          </span>
          <span className="meta" aria-label={t('number_of_likes') || 'Likes'}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {element.number_of_likes}
          </span>
          {element.created && (
            <span className="meta" aria-label={t('createdOn') || 'Erstellt am'}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                <rect x="3" y="4" width="18" height="18" rx="2" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="16" y1="2" x2="16" y2="6" stroke="#6B7280" strokeWidth="2" strokeLinecap="round"/>
                <line x1="8" y1="2" x2="8" y2="6" stroke="#6B7280" strokeWidth="2" strokeLinecap="round"/>
                <line x1="3" y1="10" x2="21" y2="10" stroke="#6B7280" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              {formatDate(element.created, currentLanguage)}
            </span>
          )}
        </div>
        <div className="ep-tags">
          {Number(element?.level_complexity) > 0 && (
            <span className="chip chip-beginner">
              {Number(element.level_complexity) === 1 && (t('beginner') || 'Beginner')}
              {Number(element.level_complexity) === 2 && (t('intermediate') || 'Intermediate')}
              {Number(element.level_complexity) === 3 && (t('advanced') || 'Advanced')}
              {![1,2,3].includes(Number(element.level_complexity)) && `${t('levelComplexity') || 'Level Complexity'} ${element.level_complexity}`}
            </span>
          )}
          {tags.map((tag, idx) => (
            <span key={`${tag}-${idx}`} className="chip chip-safety">{tag}</span>
          ))}
        </div>
        
        {/* --- NEU: Lokalisierte Beschreibung anzeigen --- */}
        {displayDesc && displayDesc.trim() && (
          <p className="ep-desc" style={{whiteSpace: 'pre-wrap'}}>{displayDesc}</p>
        )}
        {/* Progress shown in hero overlay before playback when available */}
      </section>

      {element.qr_direct_url && (() => {
        // NiimBot Button-Konfiguration (identisch zu ElementCreator)
        const niimbotBtnCfg = {
          idle:       { label: t('printQRLabel') || '🖨️ NiimBot drucken',       style: { background: '#6d28d9', color: '#fff' }, disabled: false },
          connecting: { label: t('printerConnecting') || '🔵 Verbinde...',       style: { background: '#a78bfa', color: '#fff' }, disabled: true  },
          printing:   { label: t('printerPrinting') || '⏳ Druckt...',           style: { background: '#a78bfa', color: '#fff' }, disabled: true  },
          done:       { label: t('printerDone') || '✅ Gedruckt!',               style: { background: '#10b981', color: '#fff' }, disabled: false },
          error:      { label: t('printerError') || '❌ Fehler – erneut',        style: { background: '#ef4444', color: '#fff' }, disabled: false },
        }[printStatus] || { label: '🖨️ NiimBot drucken', style: { background: '#6d28d9', color: '#fff' }, disabled: false };

        return (
          <section className="ep-card ep-qr-block">
            {/* Hidden canvas für NiimBot-Rendering */}
            <canvas ref={printCanvasRef} style={{ display: 'none' }} />

            <div className="ep-qr-title">{t('qrCode') || 'QR-Code'}</div>
            <div className="ep-qr-content">
              <img
                id="ep-qr-image"
                src={getSecureMediaUrl(element.qr_direct_url)}
                alt="QR-Code"
                className="ep-qr-img"
              />

              {niimbotAvailable ? (
                /* ── NiimBot Bluetooth-Druck ── */
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', width: '100%' }}>
                  <button
                    className="ep-qr-print-btn"
                    style={{ ...niimbotBtnCfg.style, cursor: niimbotBtnCfg.disabled ? 'not-allowed' : 'pointer', opacity: niimbotBtnCfg.disabled ? 0.75 : 1 }}
                    onClick={printQRNiimbot}
                    disabled={niimbotBtnCfg.disabled}
                    aria-label="QR-Label per NiimBot drucken"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path d="M6 9V2h12v7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <rect x="6" y="14" width="12" height="8" rx="1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {niimbotBtnCfg.label}
                  </button>
                  {printStatus === 'idle' && !printerConnected && (
                    <p style={{ margin: 0, fontSize: '0.72rem', color: '#9ca3af', textAlign: 'center' }}>
                      {t('bluetoothHint') || 'Verbindet per Bluetooth – NiimBot B1 in der Liste auswählen'}
                    </p>
                  )}
                  {printStatus === 'error' && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', width: '100%' }}>
                      <p style={{ margin: 0, fontSize: '0.72rem', color: '#ef4444', textAlign: 'center' }}>
                        {t('printerErrorHint') || 'NiimBot nicht erkannt. Drucker eingeschaltet und in Reichweite?'}
                      </p>
                      <button
                        className="ep-qr-print-btn"
                        onClick={printQROsDialog}
                        style={{ background: '#6B7280', fontSize: '13px', padding: '8px 16px' }}
                        aria-label="Standard-Druckdialog öffnen"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
                          <path d="M6 9V2h12v7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <rect x="6" y="14" width="12" height="8" rx="1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Standard-Druck (OS)
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                /* ── Standard OS-Druckdialog ── */
                <button
                  className="ep-qr-print-btn"
                  onClick={printQROsDialog}
                  aria-label={t('printQrCode') || 'QR-Code drucken'}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path d="M6 9V2h12v7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <rect x="6" y="14" width="12" height="8" rx="1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {t('printQrCode') || 'QR-Code drucken'}
                </button>
              )}
            </div>
          </section>
        );
      })()}

      {/* ── QUELLDOKUMENT-SEKTION ── */}

      {element.source_document && (() => {
        const doc = element.source_document;
        const rawDocUrl = doc.download_url || doc.file_url || null;
        if (!rawDocUrl) return null;
        // JWT als Query-Parameter für direkten Browser-Tab-Download
        const _dlToken = localStorage.getItem('token') || localStorage.getItem('hejvi_access_token') || '';
        const docUrl = _dlToken
          ? rawDocUrl + (rawDocUrl.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(_dlToken)
          : rawDocUrl;

        const ext = (doc.mime_type || '').includes('pdf') ? 'PDF'
          : (doc.mime_type || '').includes('presentation') || (doc.original_name || '').match(/\.pptx?$/i) ? 'PPTX'
          : (doc.mime_type || '').includes('word') || (doc.original_name || '').match(/\.docx?$/i) ? 'DOCX'
          : 'DOC';

        const iconColor = ext === 'PDF' ? '#E84040'
          : ext === 'PPTX' ? '#D04A02'
          : '#2A6EBB';

        const sizeStr = doc.file_size
          ? (doc.file_size >= 1048576
              ? `${(doc.file_size / 1048576).toFixed(1)} MB`
              : `${Math.round(doc.file_size / 1024)} KB`)
          : '';

        return (
          <section className="ep-card ep-document-block" style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6B7280', marginBottom: '10px' }}>
              {t('sourceDocument') || 'Quelldokument'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {/* File type icon */}
              <div style={{
                width: '44px', height: '52px', borderRadius: '6px',
                background: iconColor, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                boxShadow: '0 2px 6px rgba(0,0,0,0.25)'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
                <span style={{ color: '#fff', fontSize: '9px', fontWeight: 800, letterSpacing: '0.02em', marginTop: '2px' }}>{ext}</span>
              </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '14px', fontWeight: 600, color: '#111827',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                }}>
                  {doc.doc_name_generated || doc.doc_theme || doc.original_name || t('document') || 'Dokument'}
                </div>
                {sizeStr && (
                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>{sizeStr}</div>
                )}
                {doc.doc_theme && !doc.summary && (
                  <div style={{
                    display: 'inline-block', marginTop: '4px',
                    fontSize: '11px', fontWeight: 600, padding: '2px 7px',
                    borderRadius: '12px', background: '#F3F4F6', color: '#6B7280',
                    border: '1px solid #E5E7EB',
                  }}>
                    {doc.doc_theme}
                  </div>
                )}
                {doc.summary && (
                  <div style={{ fontSize: '12px', color: '#4B5563', marginTop: '4px', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {doc.summary}
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0 }}>
                {/* View button */}
                <a
                  href={docUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: '38px', height: '38px', borderRadius: '50%',
                    background: '#F3F4F6', border: '1.5px solid #E5E7EB',
                    color: '#374151', textDecoration: 'none',
                  }}
                  aria-label={t('viewDocument') || 'Dokument ansehen'}
                  title={t('viewDocument') || 'Ansehen'}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                </a>
                {/* Download button */}
                <a
                  href={docUrl}
                  download={doc.original_name || true}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: '38px', height: '38px', borderRadius: '50%',
                    background: '#FF6A00', border: 'none',
                    color: '#fff', textDecoration: 'none',
                    boxShadow: '0 2px 8px rgba(255,106,0,0.35)',
                  }}
                  aria-label={t('downloadDocument') || 'Dokument herunterladen'}
                  title={t('downloadDocument') || 'Herunterladen'}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                </a>
              </div>
            </div>
          </section>
        );
      })()}

      {chapters && chapters.length > 0 && (
        <section className="ep-steps">
          <div className="section-title">{t('stepsInThisVideo') || 'Steps in this video'}</div>
          <div className="steps-list">
            {chapters.map((c, idx) => {
              return (
                <button 
                  key={c.id || idx} 
                  className="step-item step-item-clickable"
                  onClick={() => handleChapterClick(c)}
                  aria-label={`${t('playFrom') || 'Play from'} ${c.name} ${formatTime(c.start_time || 0)}`}
                >
                  <div className="step-index">{idx + 1}</div>
                  <div className="step-name">{c.name}</div>
                  <div className="step-time">{formatTime(c.start_time || 0)}</div>
                  <div className="step-dot">•</div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {related && related.length > 0 && (
        <section className="ep-related">
          <div className="section-title">{t('relatedVideos') || 'Related Videos'}</div>
          <div className="related-grid">
            {related.map((r, idx) => {
              // --- NEU: Auch verwandte Videos übersetzen ---
              const relatedName = getLocalizedContent(r, 'name', currentLanguage) || r.name;
              
              return (
                <button
                  key={r.id || idx}
                  className="related-card"
                  onClick={() => navigate(`/element/${r.hash_id}`, { state: { elementData: r } })}
                >
                  <img src={getSecureMediaUrl(r.url_thumbnail)} alt={relatedName} />
                  <div className="related-name">{relatedName}</div>
                </button>
              );
            })}
          </div>
        </section>
      )}
      </div>
    </GlobalLayout>
  );
};

export default ElementPage;